<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { defineRule, ErrorMessage, Field, useForm } from 'vee-validate';
import { ToastTypes, QuestionPhone, PrimaryButton, QuestionEmail, ContactPhone, LocationSelector } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import EffectiveDateSelector from '@/quote/infrastructure/components/EffectiveDateSelector.vue';
import { EffectiveDatesResponse } from '@/application/shared/domain/EffectiveDatesResponse';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import LetsBeginReadOnly from '@/application/smallBusiness/infrastructure/components/LetsBeginReadOnly.vue';
import { useCheckCoverage } from '@/quote/infrastructure/composition/useCheckCoverage';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();

const { appStore, appState } = useAppStore();
const { checkCoverage } = useCheckCoverage();

const locationSelectorRef = ref<InstanceType<typeof LocationSelector> | null>(null);

const { handleSubmit } = useForm();
const { getIaFQuoteUrl } = useUrlHelper();

const runValidations = handleSubmit(() => true);
const size = ref(smallBusinessState.value.companySize || undefined);
let updateApplicants = false;

const effectiveDateOptions = ref<Array<EffectiveDatesResponse>>([]);

const phoneRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);

const handleFormChange = (value: unknown, field: string): void => {
  smallBusinessStore.updateCompany({ [field]: value });
};

const updateCompanyAddress = (value: unknown, field: string): void => {
  handleFormChange({ ...smallBusinessState.value.application.company.main_address, [field]: value }, 'main_address');
};

const sectionSubmit = async (): Promise<boolean> => {
  if (smallBusinessState.value.isReadOnly) {
    return true;
  }

  const valid = await runValidations();
  const locationValid = locationSelectorRef.value?.isValidLocation();
  const phoneValid = await phoneRef.value?.handleSubmit();
  const stepValid = !!(valid && locationValid && phoneValid && !appState.value.loadingCoverage);

  if (stepValid) {
    if (updateApplicants) {
      await smallBusinessStore.removeAllEmployees();
      await smallBusinessStore.addEmployee(size.value);
      await smallBusinessStore.saveApplicationOnLocalStorage();
    }
    smallBusinessStore.sendLeadInfo();

    return true;
  }

  return false;
};

const setEffectiveDateOptions = async (): Promise<void> => {
  const result = await appStore.getEffectiveDates(APPLICATION_TYPES.SMALL_BUSINESS);
  if (result.error) {
    appStore.addToast({ type: ToastTypes.DANGER, message: 'Error getting Effective Dates. Please try again.' });
  }
  if (result.success && result.data) {
    effectiveDateOptions.value = result.data;
  }
};

const setCompanySize = (): void => {
  if (size.value) {
    updateApplicants = Boolean(smallBusinessState.value.companySize !== Number(size.value));
    smallBusinessStore.setCompanySize(Number(size.value));
  }
};

const updateCompanySize = (): void => {
  smallBusinessStore.setCompanySize(smallBusinessState.value.application.applicants.length);
  if (smallBusinessState.value.companySize > 0) {
    size.value = smallBusinessState.value.companySize;
  }
};

defineRule('applicantLength', (val: string): string | boolean => {
  const value = Number(val);
  const applicants = smallBusinessState.value.application.applicants.length;
  if (value && (value >= 0 && value <= 1)) {
    return 'individualPlan';
  }
  if (!smallBusinessState.value.application.detailed_employees || (applicants === 0 || applicants <= value)) {
    return true;
  }

  return 'censusMismatch';
});

onMounted(async () => {
  await setEffectiveDateOptions();
  updateCompanySize();
});

defineExpose({ sectionSubmit });

</script>
<template>
  <BaseSection class="flex-1 flex" title="Let's begin with the basics">
    <div v-if="!smallBusinessState.isReadOnly">
      <div class="flex flex-col lg:flex-row lg:space-x-8 mt-4">
        <div class="lg:w-80 mb-4">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Number of Employees <span class="font-serif">*</span>
          </label>
          <Field v-model="size" type="number" v-slot="{ field, meta }" rules="applicantLength|between:2,50|required" id="companySize" name="companySize">
            <input type="text" inputmode="numeric" v-capitalized="field" v-bind="field" :class="{ ...meta }" @change="setCompanySize">
          </Field>
          <ErrorMessage name="companySize" v-slot="{ message }">
            <div v-if="message === 'individualPlan'" >
              <p class="text-slate-800 text-sm">
                <b>You May Qualify for an Individual Plan</b><br/>
                To be eligible for business coverage, you must have 2 employees.<br/>
                If this is not true for your company yet, click here:<br/>
              </p>
              <div class="flex">
                <PrimaryButton class="mt-2 mb-3" :href="getIaFQuoteUrl()">Get Individual & Family Quotes</PrimaryButton>
              </div>
            </div>
            <div v-else-if="message === 'censusMismatch'">
              <p class="text-red-600">
                You have more people on your census ({{smallBusinessState.application.applicants.length}}) than the number of employees you are stating. Please adjust your census to match number of employees.
              </p>
            </div>
            <span v-else class="error" v-html="message"></span>
          </ErrorMessage>
        </div>
        <div>
          <LocationSelector
            class="gap-4"
            ref="locationSelectorRef"
            id="company-info"
            placeholder=""
            :validate-coverage="true"
            :hide-labels="false"
            :show-required-on-label="true"
            :zipcode="smallBusinessState.application.company.main_address?.zip_code"
            :county="smallBusinessState.application.company.main_address?.county"
            :application-type="APPLICATION_TYPES.SMALL_BUSINESS"
            :show-coverage-error="true"
            :check-coverage="checkCoverage"
            @update:zip-code="updateCompanyAddress($event, 'zip_code')"
            @update:county="updateCompanyAddress($event, 'county')" />
        </div>
      </div>
      <div class="flex flex-col lg:flex-row lg:space-x-8 mt-8 lg:mt-0">
        <div class="lg:w-80">
         <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Email Address <span class="font-serif">*</span>
          </label>
          <QuestionEmail :value="smallBusinessState.application.company.primary_contact?.email" :disable-placeholder="true" @update:value="(v: string) => handleFormChange({ ...smallBusinessState.application.company.primary_contact, email: v}, 'primary_contact')" :use-form-context="true" id="company-email" />
        </div>
        <div class="">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Phone <span class="font-serif">*</span>
          </label>
          <QuestionPhone :value="{ number: smallBusinessState.application.company.primary_contact?.phone }" phone-input-placeholder="" @update:value="(v: ContactPhone) => handleFormChange({ ...smallBusinessState.application.company.primary_contact, phone: v.number}, 'primary_contact')" ref="phoneRef" :hide-ext="true" :hide-type="true" />
        </div>
      </div>
      <div class="flex flex-col lg:flex-row space-x-4" :class="{ 'mt-4': smallBusinessState.isReadOnly }">
        <EffectiveDateSelector
          v-if="effectiveDateOptions.length > 0"
          :effective-date="smallBusinessState.application.effective_date"
          :options="effectiveDateOptions"
          @update:effectiveDate="effectiveDate => smallBusinessStore.setEffectiveDate(effectiveDate)"/>
      </div>
    </div>
    <LetsBeginReadOnly v-else />
  </BaseSection>
</template>
